import { Injectable } from '@angular/core';
import { CollectionForm } from '../../../../../models/ts/collection-form.model';
import { CollectionFormField } from '../../../../../models/ts/collection-form-field.model';
import { CollectionFormService } from './collection-form.service';
import { UpdateDeleteState } from '../../../../../models/ts/update-delete-state.model';
import { ViewDataSource } from '../../../../../models/ts/view-data-source.model';
import { ViewDataSourcesInstance } from '../../../../../models/ts/view-data-sources-instance.model';
import { LinkedCollectionType } from '../../../../../models/ts/linked-collection-type.model';
import { CollectionFormLinkedService } from './collection-form-linked.service';
import { filter, isEmpty } from 'underscore';
import { LinkedCollectionStorageType } from '../../../../../models/ts/linked-collection-storage-type.model';

/**
 * Service for converting CollectionForm objects to JSON. Functions return plain objects formatted as the back end expects.
 */
@Injectable({
  providedIn: 'root'
})
export class CollectionFormJsonService {

  /** * Adds dummy instances to historical fields that have no instances.   *
   *
   * Fields can have vds links but when assigned a value these wont be picked up in instance creates
   * this logic performs a 1 time operation initially to add dummy instances to historical fields
   * this way they get picked up by the mapToJson function to be passed trough to the back end.
   *
   * @param {CollectionForm} form
   * @returns {CollectionForm}
   */
  public static addDummyInstancesToHistoricalFields(form: CollectionForm): CollectionForm {
    let updatedForm = structuredClone(form);
    // get all historical VDSs
    const fields = CollectionFormService.extractFieldsFromForm(form);
    const emptyHistoricalVds = updatedForm.ViewDataSources.filter(
      vds => vds.LinkedCollectionStorageType == LinkedCollectionStorageType.Historical
        && vds.Instances.length == 0
        && !vds.IsSnapshotRelation
        && vds.SingleOrMany == LinkedCollectionType.SingleRecord
        && !vds.MustBeInList
        && !CollectionFormJsonService.viewDataSourceHasSnapshotChildInstances(updatedForm, vds)
        && fields?.find(field => field.ViewDataSourcesID == vds.ViewDataSourcesID)
    );

    if (emptyHistoricalVds.length != 0) {
      const instance = {
        CrossLinkedInstancesID: -1,
        isDummyGenerated: true
      } as ViewDataSourcesInstance;

      emptyHistoricalVds.forEach(vds => vds.Instances = [instance]);
    }

    return updatedForm;
  }

  /**
   * Returns a forms JSON representation
   * @param {CollectionForm} form
   * @return {Object}
   */
  public static formToJson(form: CollectionForm, historicalEditInStackSettings?: {IsHistoricalEdit: boolean, HasRelationalAncestor: boolean, HasHistoricalAncestor: boolean } | null): Object {
    let formJson: Object = {};
    form = this.addDummyInstancesToHistoricalFields(form);
    // Base form fields
    Object.assign(formJson, CollectionFormJsonService.baseFieldsToJson(form));
    // Fixed properties
    Object.assign(formJson, CollectionFormJsonService.formPropertiesToJson(form));
    // Relations (ViewDataSources)
    Object.assign(formJson, CollectionFormJsonService.viewDataSourcesToJson(form));
    if(historicalEditInStackSettings != null){
      Object.assign(formJson, {HistoricalEditInStackSettings: historicalEditInStackSettings});
    }
    return formJson;
  }

  /**
   * Returns a fields JSON representation.
   * @param {CollectionFormField} field
   * @param {ViewDataSourcesInstance} instance
   * @return {Object}
   */
  public static fieldToJson(field: CollectionFormField, instance?: ViewDataSourcesInstance): Object {
    const fieldJson = CollectionFormJsonService.getJsonForField(field, instance);
    return field.State != UpdateDeleteState.Update && isEmpty(fieldJson) && !field.IsHidden ? {} : { [field.Bookmark]: fieldJson };
  }

  /**
   * Returns an array of JSON representation of all fields not part of a relation.
   * @param {CollectionForm} form
   * @return {Object}
   */
  public static baseFieldsToJson(form: CollectionForm): Object {
    const fields = this.getFieldsWithValue(CollectionFormService.getFields(form, field => field.ViewDataSourcesID == 0 && !CollectionFormService.fieldIsGrid(field)));
    let jsonFields: Object = {};
    fields.forEach(field => {
      let fieldJson = CollectionFormJsonService.fieldToJson(field);
      if (Object.keys(fieldJson).length > 0){
        Object.assign(jsonFields, fieldJson);
      }



    });
    return jsonFields;
  }

  public static getFieldsWithValue(fields: Array<CollectionFormField>): Array<CollectionFormField> {
    return filter(fields, function (field) {
      return ((field.Value != undefined && field.Value != null && field.Value !== '') || field.State == UpdateDeleteState.Update) || field.OrgChartUnitSelector.length > 0 || (field.IsHidden || field.IsRequired || field.Evaluations.length > 0) || (field.Bookmark != null && field.Bookmark.toLowerCase() == 'mailinglist');
    });
  }

  /**
   * Returns an array of JSON representation of a number of form properties.
   * @param {CollectionForm} form
   * @return {Object}
   */
  public static formPropertiesToJson(form: CollectionForm): Object {
    const propertiesObject = {
      DocumentProperties: form.DocumentProperties,
      InheritType: form.InheritType,
      DisplayFieldsInheritType: form.DisplayFieldsInheritType,
      ExamSubscriptions: form.ExamSubscriptions,
      TrainingAssessment: form.TrainingAssessment
    };
    // TODO: Back end should handle 0 value correctly. (CollectionJsonMapper => AddFieldsToBaseCollectionsModel => ?m.IsLookup = true?)
    if (form.VersionsID > 0)
      Object.assign(propertiesObject, { versionsID: form.VersionsID });
    return propertiesObject;

  }

  /**
   * Returns the JSON representation of all ViewDataSources in given form.
   * @param {CollectionForm} form
   * @return {Object}
   */
  public static viewDataSourcesToJson(form: CollectionForm): Object {
    let viewDataSourcesJson: Object = {};
    // Only top level ViewDataSources that are linked to the form collection are useful for JSON
    form.ViewDataSources.filter(vds => vds.ParentCollectionsID == form.CollectionsID && vds.ParentDataSourcesID == 0).forEach(viewDataSource => {
      Object.assign(viewDataSourcesJson, CollectionFormJsonService.viewDataSourceToJson(form, viewDataSource));
    });
    return viewDataSourcesJson;
  }

  /**
   * Returns the JSON representation of a ViewDataSource.
   * @param {CollectionForm} form
   * @param {ViewDataSource} viewDataSource
   * @param {number} rowDataDesignCrossId
   * @return {Object}
   */
  public static viewDataSourceToJson(form: CollectionForm, viewDataSource: ViewDataSource, rowDataDesignCrossId: number = 0): Object {
    let instancesJson = {};
    if (viewDataSource.ParentDataSourcesID == 0) {
      instancesJson = CollectionFormJsonService.viewDataSourceInstancesToJson(form, viewDataSource);
    } else {
      instancesJson = CollectionFormJsonService.deeperLevelViewDataSourceInstancesToJson(form, viewDataSource, rowDataDesignCrossId);
    }
    return isEmpty(instancesJson) ? {} : { [viewDataSource.Name]: instancesJson };
  }

  public static getJsonInstances(viewDataSourceInstances: ViewDataSourcesInstance[], snapshotViewDataSourceInstances: ViewDataSourcesInstance[]): Array<ViewDataSourcesInstance> {
    // Clone the snapshots since we need to alter some ID's that shouldn't reflect on the state.
    let workingSnapshots = structuredClone(snapshotViewDataSourceInstances);
    // Created Instance
    // Snapshot VDS Instance with RowDataDesignCrossID in negative (will not have normal instance)
    // => Use Snapshot
    let createdInstances = workingSnapshots.filter(snapshotInstance => viewDataSourceInstances.findIndex(instance => instance.RowDataDesignCrossID == snapshotInstance.RowDataDesignCrossID) == -1);

    // Updated Instance
    // VDS Instance + Snapshot VDS Instance with same RowDataDesignCrossID
    // Snapshot State = 1
    // => Use Snapshot (!! Update some ID's for JSON serialization to work correctly for API !!)
    // Snapshot state = 0
    // => Use instance
    let updatedInstances: ViewDataSourcesInstance[] = [];
    let unchangedInstances: ViewDataSourcesInstance[] = [];

    // Deleted Instance
    // VDS Instance, NO Snapshot VDS Instance with same RowDataDesignCrossID
    // Set State to Deleted
    // => Use Instance
    let deletedInstances: ViewDataSourcesInstance[] = [];

    // Both Updated and Deleted instance can be found via normal VDS Instances array
    viewDataSourceInstances.forEach(instance => {
      let snapshot = workingSnapshots.find(snapshotInstance => snapshotInstance.RowDataDesignCrossID == instance.RowDataDesignCrossID);
      if (snapshot !== undefined) {
        if (snapshot.State == UpdateDeleteState.Update) {
          snapshot.CrossLinkedInstancesID = instance.CrossLinkedInstancesID;
          snapshot.OriginalChildInstancesID = instance.OriginalChildInstancesID;
          updatedInstances.push(snapshot);
        }
        //
        else unchangedInstances.push(instance);
      } else {
        deletedInstances.push(instance);
      }
    });
    return createdInstances.concat(updatedInstances, unchangedInstances, deletedInstances);
  }

  /**
   * Returns instances belonging to a deeper level VDS.
   * @param {CollectionForm} form
   * @param {ViewDataSource} viewDataSource
   * @return {Object}
   */
  public static deeperLevelViewDataSourceInstancesToJson(form: CollectionForm, viewDataSource: ViewDataSource, rowDataDesignCrossId: number): Object[] {
    const parentGridViewDataSource = CollectionFormLinkedService.getParentGridViewDataSource(form, viewDataSource);
    const snapshotViewDataSource = form.ViewDataSources.find(vds => vds.IsSnapshotRelation && vds.ParentDataSourcesID == viewDataSource.ViewDataSourcesID);
    let viewDataSourceInstancesJson: Object[] = [];
    if (snapshotViewDataSource !== undefined) {
      let viewDataSourceInstances = viewDataSource.Instances;
      let snapshotViewDataSourceInstances = snapshotViewDataSource.Instances;
      // If there is a parent grid, only instances that belong to the current row should be included
      if (parentGridViewDataSource !== undefined) {
        viewDataSourceInstances = viewDataSourceInstances.filter(i => i.RowDataDesignCrossID == rowDataDesignCrossId);
        snapshotViewDataSourceInstances = snapshotViewDataSourceInstances.filter(i => i.RowDataDesignCrossID == rowDataDesignCrossId);
      }
      CollectionFormJsonService.getJsonInstances(
        viewDataSourceInstances,
        snapshotViewDataSourceInstances
      ).forEach(instance => {
        let viewDataSourceInstance = {};
        // Fields, if any parent is a grid, grid vds is required as well
        if (parentGridViewDataSource !== undefined)
          Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceInstanceGridFieldsToJson(form, viewDataSource, parentGridViewDataSource, instance));
        else
          Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceInstanceFieldsToJson(form, viewDataSource, instance));
        // Properties
        Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceInstancePropertiesToJson(viewDataSource, instance));
        // Children
        Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceChildrenToJson(form, snapshotViewDataSource, rowDataDesignCrossId));
        // Parental? non snapshot -> has to do with inner childs: https://bizzmine.slack.com/archives/C17HTUC94/p1748349189862229
        if (form.ViewDataSources.find(vds => vds.ParentDataSourcesID == viewDataSource.ViewDataSourcesID && !vds.IsSnapshotRelation)) {
          Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceChildrenToJson(form, viewDataSource, rowDataDesignCrossId));
        }

        viewDataSourceInstancesJson.push(viewDataSourceInstance);
      });
    }

    return viewDataSourceInstancesJson;
  }

  /**
   * Returns a ViewDataSource's instances in JSON representation.
   * @param {CollectionForm} form
   * @param viewDataSource
   * @return {Object[]}
   */
  public static viewDataSourceInstancesToJson(form: CollectionForm, viewDataSource: ViewDataSource): Object[] {
    let viewDataSourceInstancesJson: Object[] = [];
    viewDataSource.Instances.forEach(instance => {
      let viewDataSourceInstance = {};
      // Fields
      Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceInstanceFieldsToJson(form, viewDataSource, instance));
      // Properties
      Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceInstancePropertiesToJson(viewDataSource, instance));
      // Children
      Object.assign(viewDataSourceInstance, CollectionFormJsonService.viewDataSourceChildrenToJson(form, viewDataSource, instance.RowDataDesignCrossID));
      viewDataSourceInstancesJson.push(viewDataSourceInstance);
    });
    return viewDataSourceInstancesJson;
  }

  /**
   * Returns the JSON representation of a ViewDataSource's children.
   * @param {CollectionForm} form
   * @param {ViewDataSource} viewDataSource
   * @param rowDataDesignCrossID
   * @return {Object}
   */
  public static viewDataSourceChildrenToJson(form: CollectionForm, viewDataSource: ViewDataSource, rowDataDesignCrossID: number): Object {
    let childViewDataSources = form.ViewDataSources.filter(vds => vds.ParentDataSourcesID == viewDataSource.ViewDataSourcesID && !vds.IsSnapshotRelation);
    if (childViewDataSources.length > 0) {
      let childViewDataSourcesJson: Object = {};
      childViewDataSources.forEach(childViewDataSource => {
        if (CollectionFormJsonService.viewDataSourceHasChildInstances(form, childViewDataSource))
          this.assignAndMergeArrays(childViewDataSourcesJson, CollectionFormJsonService.viewDataSourceToJson(form, childViewDataSource, rowDataDesignCrossID));
        else if (CollectionFormJsonService.viewDataSourceHasSnapshotChildInstances(form, childViewDataSource)) {
          const snapshot = form.ViewDataSources.find(vds => vds.IsSnapshotRelation && vds.ParentDataSourcesID == childViewDataSource.ViewDataSourcesID);
          if (snapshot) {
            const updatedChildViewDataSource = structuredClone(childViewDataSource);
            updatedChildViewDataSource.Instances = updatedChildViewDataSource.Instances.concat(snapshot.Instances);
            this.assignAndMergeArrays(childViewDataSourcesJson, CollectionFormJsonService.viewDataSourceToJson(form, updatedChildViewDataSource, rowDataDesignCrossID));
          }
        }
      });
      return childViewDataSourcesJson;
    } else return {};
  }

  public static viewDataSourceHasChildInstances(form: CollectionForm, viewDataSource: ViewDataSource): boolean {
    return viewDataSource.Instances.length > 0;
  }

  public static viewDataSourceHasSnapshotChildInstances(form: CollectionForm, viewDataSource: ViewDataSource): boolean {
    const snapshot = form.ViewDataSources.find(vds => vds.IsSnapshotRelation && vds.ParentDataSourcesID == viewDataSource.ViewDataSourcesID);
    if (snapshot) {
      return snapshot.Instances.length > 0;
    } else return false;
  }

  /**
   * Returns the JSON representation of form fields belonging to given instance.
   * @param {CollectionForm} form
   * @param {ViewDataSource} viewDataSource
   * @param {ViewDataSourcesInstance} instance
   * @return {Object}
   */
  public static viewDataSourceInstanceFieldsToJson(form: CollectionForm, viewDataSource: ViewDataSource, instance: ViewDataSourcesInstance): Object {
    let fields = CollectionFormService.getFields(form, field => field.ViewDataSourcesID == viewDataSource.ViewDataSourcesID);
    let jsonFields: Object = {};
    if (viewDataSource.SingleOrMany == LinkedCollectionType.SingleRecord) {
      fields.forEach(field => {
        let fieldJson = CollectionFormJsonService.fieldToJson(field, instance);
        if (Object.keys(fieldJson).length > 0 && (instance.isDummyGenerated != true || Object.values(fieldJson)[0].value != null)) {
          Object.assign(jsonFields, fieldJson);
        }
      });
      return jsonFields;
    } else {
      // TODO: replace with IsGridField once available.
      let gridField = fields.find(field => CollectionFormService.fieldIsGrid(field));
      if (gridField !== undefined) {
        let record = gridField.Records?.find(record => record.CrossLinkedInstancesID == instance.RowDataDesignCrossID);
        if (record) {
          record.Fields.filter(field => field.ViewDataSourcesID == (viewDataSource.IsSnapshotRelation ? viewDataSource.ParentDataSourcesID : viewDataSource.ViewDataSourcesID)).forEach(field => {
            let fieldJson = CollectionFormJsonService.fieldToJson(field, instance);
            if (Object.keys(fieldJson).length > 0)
              Object.assign(jsonFields, fieldJson);
          });
        }
        return jsonFields;
      }
    }
    return {};
  }

  /**
   * Returns the JSON representation of form fields belonging to given instance.
   * @param {CollectionForm} form
   * @param {ViewDataSource} viewDataSource
   * @param {ViewDataSourcesInstance} instance
   * @return {Object}
   */
  public static viewDataSourceInstanceGridFieldsToJson(form: CollectionForm, viewDataSource: ViewDataSource, parentViewDataSource: ViewDataSource, instance: ViewDataSourcesInstance): Object {
    let jsonFields: Object = {};
    let gridField = CollectionFormService.getFields(form, field => field.ViewDataSourcesID == parentViewDataSource.ViewDataSourcesID).find(field => CollectionFormService.fieldIsGrid(field));
    if (gridField !== undefined) {
      let record = gridField.Records?.find(record => record.CrossLinkedInstancesID == instance.RowDataDesignCrossID);
      if (record) {
        record.Fields.filter(field => field.ViewDataSourcesID == (viewDataSource.IsSnapshotRelation ? viewDataSource.ParentDataSourcesID : viewDataSource.ViewDataSourcesID)).forEach(field => {
          let fieldJson = CollectionFormJsonService.fieldToJson(field, instance);
          if (Object.keys(fieldJson).length > 0)
            Object.assign(jsonFields, fieldJson);
        });
      }
      return jsonFields;
    }
    return {};
  }

  /**
   * Returns the JSON representation of a VDS instance properties.
   * @param {ViewDataSource} viewDataSource
   * @param instance
   * @return {Object}
   */
  public static viewDataSourceInstancePropertiesToJson(viewDataSource: ViewDataSource, instance: ViewDataSourcesInstance): Object {
    // temp removed historicalEditEnabled -> backend has issues
    return {
      ViewDataSourcesID: viewDataSource.ViewDataSourcesID,
      crossLinkedInstancesID: instance.CrossLinkedInstancesID,
      deleteIfExists: instance.State == UpdateDeleteState.Delete,
      originalChildInstancesID: instance.OriginalChildInstancesID,
      previousChildVersionsID: instance.PreviousChildVersionsID,
      rowDataDesignCrossID: instance.RowDataDesignCrossID,
      snapshotCrossLinkInstancesID: instance.SnapshotCrosslinkInstancesID,
      versionsID: instance.ChildVersionsID,
      HasEditInStack: instance.HasEditInStack
    };
  }

  /**
   * Returns the json representation of a field or errors out if no value could be processed to JSON.
   * @param {ViewDataSourcesInstance} instance
   * @param {CollectionFormField} field
   * @return {Object}
   */
  public static getJsonForField(field: CollectionFormField, instance?: ViewDataSourcesInstance): Object {
    let jsonField = CollectionFormService.getValueForField(field);

    if (jsonField !== null && instance !== undefined) {
      // updateIfExists
      CollectionFormJsonService.jsonFieldUpdateIfExists(jsonField, instance, field);
      // deleteIfExists
      CollectionFormJsonService.jsonFieldDeleteIfExists(jsonField, instance, field);
      return jsonField;
    } else if (instance !== undefined) {
      jsonField = { value: null };
      CollectionFormJsonService.jsonFieldDeleteIfExists(jsonField, instance, field);
      return jsonField;
    } else return jsonField ?? {};
  }

  /**
   * Mutates the given json field object with updateIfExists property if required.
   * @param {Object} jsonField
   * @param {ViewDataSourcesInstance} instance
   * @param {CollectionFormField} field
   */
  public static jsonFieldUpdateIfExists(jsonField: Object, instance: ViewDataSourcesInstance, field: CollectionFormField): void {
    // (&& viewDataSource.ParentDataSourcesID == 0) This is a check in legacy but adding it creates duplicates in 1x1xN situations
    if (!field.IsCrossLinkedField && instance.CrossLinkedInstancesID > 0)
      Object.assign(jsonField, { updateIfExists: true });
  }

  /**
   * Mutates the given json field object with updateIfExists property if required.
   * @param {Object} jsonField
   * @param {ViewDataSourcesInstance} instance
   * @param {CollectionFormField} field
   */
  public static jsonFieldDeleteIfExists(jsonField: Object, instance: ViewDataSourcesInstance, field: CollectionFormField): void {
    if (instance.CrossLinkedInstancesID > 0 && instance.State == UpdateDeleteState.Delete)
      Object.assign(jsonField, { deleteIfExists: true });
  }

  /**
   * Performs Object.assign but concatenates arrays with the same key instead of replacing them.
   * @param target
   * @param source
   * @return {any}
   * @private
   */
  private static assignAndMergeArrays(target: any, source: any): any {
    let targetKeys = new Set<string>(Object.keys(target));
    let sourceKeys = new Set<string>(Object.keys(source));
    let duplicateKeys = targetKeys.intersection(sourceKeys);

    duplicateKeys.forEach((key) => {
      if (Array.isArray(target[key]) && Array.isArray(source[key])) {
        source[key] = source[key].concat(target[key]);
      }
    });

    return Object.assign(target, source);
  }
}
